import { Card, Checkbox } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../../server";
import { RiSearchLine, RiAuctionLine, RiCalendarLine, RiEditLine, RiAddLine, RiCloseLine, RiCoinLine } from "react-icons/ri";
import convertLocalTimeToUtc from "../../utils/ConvertLocalTimeToUtc";
import { useNavigate } from "react-router-dom";

export default function Auction() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shops, setShops] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedShops, setSelectedShops] = useState([]);
  const [auctionDate, setAuctionDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const getAllShops = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/shop/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
      });
      setShops(data?.data || []);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.message || e?.message || "Failed to fetch shops");
    }
  };

  useEffect(() => {
    getAllShops();
  }, []);

  const filteredShops = shops.filter(
    (shop) =>
      shop.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.shopCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allShopIds = filteredShops.map((shop) => shop._id || shop.id);
      setSelectedShops(allShopIds);
    } else {
      setSelectedShops([]);
    }
  };

  const handleSelectShop = (shopId) => {
    setSelectedShops((prev) => {
      if (prev.includes(shopId)) {
        return prev.filter((id) => id !== shopId);
      } else {
        return [...prev, shopId];
      }
    });
  };

  const handleAddStep = () => {
    if (!currentStep) return;
    const stepNum = parseInt(currentStep);
    if (isNaN(stepNum)) {
      toast.warning("Please enter a valid number");
      return;
    }
    if (steps.includes(stepNum)) {
      toast.warning("Step already added");
      return;
    }
    setSteps([...steps, stepNum].sort((a, b) => a - b));
    setCurrentStep("");
  };

  const handleRemoveStep = (stepToRemove) => {
    setSteps(steps.filter((step) => step !== stepToRemove));
  };

  const handleStartAuction = async () => {
    // Validation
    if (selectedShops.length === 0) {
      toast.error("Please select at least one shop");
      return;
    }
    if (!auctionDate) {
      toast.error("Please select an auction date");
      return;
    }
    if (!startTime) {
      toast.error("Please select a start time");
      return;
    }
    if (!endTime) {
      toast.error("Please select an end time");
      return;
    }
    if (steps.length === 0) {
      toast.error("Please add at least one step");
      return;
    }

    // Validate that end time is greater than start time
    const startDateTime = new Date(`${auctionDate}T${startTime}`);
    const endDateTime = new Date(`${auctionDate}T${endTime}`);

    if (endDateTime <= startDateTime) {
      toast.error("End time must be greater than start time");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        shopIds: selectedShops,
        bidDate: auctionDate ? new Date(auctionDate).toISOString().split("T")[0] : null,
        openTime: startTime + ":00", // Append seconds to match HH:mm:ss format
        closeTime: endTime + ":00",  // Append seconds to match HH:mm:ss format
        steps: steps,
      };
      console.log(payload);

      const { data } = await axios.put(`${server}/shop/updateBidInfo`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
      });

      toast.success(data?.message || "Auction started successfully!");

      // Reset form
      setSelectedShops([]);
      setAuctionDate("");
      setStartTime("");
      setEndTime("");
      setSteps([]);
      setCurrentStep("");

      setSubmitting(false);
    } catch (e) {
      setSubmitting(false);
      toast.error(e?.response?.data?.message || e?.message || "Failed to start auction");
    }
  };

  const isAllSelected = filteredShops.length > 0 && selectedShops.length === filteredShops.length;
  const isSomeSelected = selectedShops.length > 0 && selectedShops.length < filteredShops.length;

  return (
    <div className="flex justify-center w-full p-4 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="w-full max-w-7xl">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-3 rounded-xl shadow-lg">
              <RiAuctionLine className="text-3xl text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-800 tracking-tight">
                Start Auction
              </h1>
              <p className="text-slate-500 mt-1">
                Select shops and set auction dates to begin
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Shop Selection Section */}
          <div className="lg:col-span-2">
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
              className="bg-white"
            >
              {/* Search Bar */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-slate-200">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <RiSearchLine className="text-slate-400 text-lg" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search shops by name or code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2.5 w-full border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 placeholder-slate-400 shadow-sm transition-all"
                  />
                </div>
              </div>

              {/* Shop List Table */}
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-700 uppercase bg-slate-100 border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-4 font-bold">
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={isSomeSelected}
                          onChange={handleSelectAll}
                          sx={{
                            color: "#3b82f6",
                            "&.Mui-checked": {
                              color: "#3b82f6",
                            },
                            "&.MuiCheckbox-indeterminate": {
                              color: "#3b82f6",
                            },
                          }}
                        />
                      </th>
                      <th className="px-6 py-4 font-bold">S.No.</th>
                      <th className="px-6 py-4 font-bold">Shop Name</th>
                      <th className="px-6 py-4 font-bold">Shop Code</th>
                      <th className="px-6 py-4 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="animate-spin h-8 w-8 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <p className="text-slate-500 font-medium">Loading shops...</p>
                          </div>
                        </td>
                      </tr>
                    ) : filteredShops.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="bg-slate-100 p-4 rounded-full mb-3">
                              <RiSearchLine className="text-2xl text-slate-400" />
                            </div>
                            <p className="text-slate-800 font-medium">No shops found</p>
                            <p className="text-slate-500 text-sm mt-1">
                              {searchTerm ? `No matches for "${searchTerm}"` : "No shops available"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredShops.map((shop, index) => {
                        const shopId = shop._id || shop.id;
                        const isSelected = selectedShops.includes(shopId);
                        return (
                          <tr
                            key={shopId || shop.shopCode || index}
                            onClick={() => handleSelectShop(shopId)}
                            className={`border-b border-slate-100 transition-all duration-150 cursor-pointer ${isSelected
                              ? "bg-blue-50 hover:bg-blue-100"
                              : "bg-white hover:bg-slate-50"
                              }`}
                          >
                            <td className="px-6 py-4">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleSelectShop(shopId)}
                                sx={{
                                  color: "#3b82f6",
                                  "&.Mui-checked": {
                                    color: "#3b82f6",
                                  },
                                }}
                              />
                            </td>
                            <td className="px-6 py-4 font-medium text-slate-900">
                              {index + 1}
                            </td>
                            <td className="px-6 py-4 text-slate-700 font-medium">
                              {shop.shopName}
                            </td>
                            <td className="px-6 py-4 text-slate-700">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                {shop.shopCode}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/my/editShopDetails", { state: { shop } });
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                  <RiEditLine className="text-sm" />
                                  Edit
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/my/bidDetails", { state: { shop } });
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 hover:border-blue-500 text-slate-600 hover:text-blue-600 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                  <RiCoinLine className="text-sm" />
                                  Bids
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
                <span className="text-sm text-slate-600 font-medium">
                  {selectedShops.length} of {filteredShops.length} shops selected
                </span>
              </div>
            </Card>
          </div>

          {/* Auction Details Section */}
          <div className="lg:col-span-1">
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
              className="bg-white sticky top-4"
            >
              <div className="p-6">
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <RiCalendarLine className="text-blue-500" />
                  Auction Details
                </h2>

                {/* Selected Shops Summary */}
                <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">
                    Selected Shops
                  </p>
                  <p className="text-3xl font-bold text-blue-600">
                    {selectedShops.length}
                  </p>
                </div>

                {/* Auction Date */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Auction Date
                  </label>
                  <input
                    type="date"
                    value={auctionDate}
                    onChange={(e) => setAuctionDate(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 shadow-sm transition-all"
                  />
                </div>

                {/* Start Time */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 shadow-sm transition-all"
                  />
                </div>

                {/* End Time */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    min={startTime || undefined}
                    onChange={(e) => {
                      const selectedEndTime = e.target.value;
                      // Only allow setting end time if it's greater than start time
                      if (!startTime || selectedEndTime > startTime) {
                        setEndTime(selectedEndTime);
                      } else {
                        toast.warning("End time must be greater than start time");
                      }
                    }}
                    disabled={!startTime}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 shadow-sm transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
                  />
                  {!startTime && (
                    <p className="text-xs text-slate-500 mt-1">
                      Please select a start time first
                    </p>
                  )}
                </div>

                {/* Steps */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Steps (Array of Integers)
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="number"
                      value={currentStep}
                      onChange={(e) => setCurrentStep(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAddStep()}
                      placeholder="Enter step"
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 shadow-sm transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleAddStep}
                      className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                    >
                      <RiAddLine className="text-xl" />
                    </button>
                  </div>

                  {/* Display Added Steps */}
                  <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-slate-50 border border-dashed border-slate-200 rounded-lg">
                    {steps.length === 0 ? (
                      <span className="text-xs text-slate-400 italic">No steps added yet</span>
                    ) : (
                      steps.map((step) => (
                        <span
                          key={step}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium border border-blue-200 animate-in fade-in zoom-in duration-200"
                        >
                          {step}
                          <button
                            onClick={() => handleRemoveStep(step)}
                            className="hover:text-red-500 transition-colors"
                          >
                            <RiCloseLine className="text-base" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Start Auction Button */}
                <button
                  onClick={handleStartAuction}
                  disabled={submitting || selectedShops.length === 0}
                  className={`w-full py-3 px-6 rounded-lg font-bold text-white shadow-lg transition-all duration-200 flex items-center justify-center gap-2 ${submitting || selectedShops.length === 0
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 hover:shadow-xl transform hover:-translate-y-0.5"
                    }`}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Starting...
                    </>
                  ) : (
                    <>
                      <RiAuctionLine className="text-xl" />
                      Start Auction
                    </>
                  )}
                </button>

                {/* Info Box */}
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800">
                    <span className="font-semibold">Note:</span> Make sure to select shops and set valid dates before starting the auction.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}